import React from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import Hero from "../landing/Hero";
import ProblemSection from "../landing/ProblemSection";
import SolutionSection from "../landing/SolutionSection";
import HowItWorks from "../landing/HowItWorks";
import FeaturesSection from "../landing/FeaturesSection";
import BenefitsSection from "../landing/BenefitsSection";
import CtaSection from "../landing/CtaSection";
import SeoHead from "../components/SeoHead";
import { useAuth } from "../context/AuthContext";
import { readSessionHint } from "../utils/sessionHint";

const landingJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Paysafi",
    url: "https://paysafi.com",
    logo: "https://paysafi.com/logo.svg",
    description:
      "Paysafi is a rent tracking and tenant management platform built for Kenyan landlords, with automated SMS reminders and AI voice calls.",
    areaServed: "KE",
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Paysafi",
    url: "https://paysafi.com",
  },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const viewingFromApp = searchParams.get("view") === "landing";

  // Signed-in users typing paysafi.com land straight on the dashboard —
  // no reason to show them marketing. But if they explicitly navigated
  // via the sidebar logo (?view=landing), respect that and show the
  // landing page.
  //
  // The hint lets us redirect optimistically on the FIRST render for
  // returning signed-in users, instead of holding the page blank while
  // /auth/me resolves. Snapshot once with useMemo so the AuthContext
  // sync write can't flip the decision mid-render. Stale-hint fallback:
  // if the cookie expired, RequireAuth on /app/* sees !user after
  // /auth/me and bounces to /signin — same UX as today.
  const hasSessionHint = React.useMemo(() => readSessionHint(), []);
  if (!viewingFromApp && (user || (loading && hasSessionHint))) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <>
      <SeoHead
        title="Paysafi | Rent Tracking and Tenant Management for Kenyan Landlords"
        description="Track rent, manage tenants, and collect payments with automated SMS reminders and AI voice calls. Built for Kenyan landlords who want less chasing and more paying."
        path="/"
        jsonLd={landingJsonLd}
      />
      <Hero />
      <ProblemSection />
      <SolutionSection />
      <HowItWorks />
      <FeaturesSection />
      <BenefitsSection />
      <CtaSection />
    </>
  );
}
